// ABC SEO/GEO — Admin: client management, plan/status overrides & discounts.
// Master-key only (gated in SGApp). Lets AI Biz Connect comp, discount, upgrade
// or downgrade any client without breaking their access.
(function () {
  const { Icon } = window.SGIcons;
  const { Card, CardHead, PageHead } = window.SGKit;

  const PLANS = ['starter', 'growth', 'agency'];
  const STATUSES = ['trial', 'active', 'past_due', 'canceled'];
  const PLAN_PRICE = { starter: 49, growth: 149, agency: 399 };

  function StatusPill({ s }) {
    const m = {
      active: { c: 'var(--sg-green)', bg: 'var(--sg-green-50)' },
      trial: { c: 'var(--sg-amber)', bg: 'var(--sg-amber-50)' },
      past_due: { c: 'var(--sg-red, #DC2626)', bg: 'var(--sg-red-50, #FDECEC)' },
      canceled: { c: 'var(--sg-text-3)', bg: 'var(--sg-sunken)' },
    }[s] || { c: 'var(--sg-text-2)', bg: 'var(--sg-sunken)' };
    return <span style={{ fontSize: 11.5, fontWeight: 700, color: m.c, background: m.bg, padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)' }}>{s}</span>;
  }

  function field(label, control) {
    return (
      <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--sg-text-3)' }}>{label}</span>
        {control}
      </label>
    );
  }
  const selStyle = { height: 38, padding: '0 10px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-card)', fontFamily: 'var(--sg-font-sans)', fontSize: 13, color: 'var(--sg-text)' };
  const inStyle = { ...selStyle, width: '100%' };

  function netPrice(plan, discount) {
    const base = PLAN_PRICE[plan] || 0;
    const net = Math.round(base * (1 - (discount || 0) / 100));
    return { base, net };
  }

  function ClientCard({ c, onSaved }) {
    const [plan, setPlan] = React.useState(c.plan || 'starter');
    const [status, setStatus] = React.useState(c.status || 'trial');
    const [limit, setLimit] = React.useState(c.domain_limit ?? 1);
    const [discount, setDiscount] = React.useState(c.discount_pct ?? 0);
    const [notes, setNotes] = React.useState(c.admin_notes || '');
    const [kw, setKw] = React.useState(c.keyword_limit ?? 25);
    const [rank, setRank] = React.useState(c.ranking_limit ?? 25);
    const [wl, setWl] = React.useState(!!c.whitelabel);
    const [brandName, setBrandName] = React.useState(c.brand_name || '');
    const [accent, setAccent] = React.useState(c.accent_color || '#2563EB');
    const [wlEmail, setWlEmail] = React.useState(c.support_email || '');
    const [wlPhone, setWlPhone] = React.useState(c.support_phone || '');
    const [logo, setLogo] = React.useState(c.logo_url || '');
    const [busy, setBusy] = React.useState(false);
    const [link, setLink] = React.useState(null);
    const [msg, setMsg] = React.useState(null);
    const [actBusy, setActBusy] = React.useState(false);
    const [confirmDel, setConfirmDel] = React.useState(false);
    const [delText, setDelText] = React.useState('');
    const [pwVal, setPwVal] = React.useState('');
    const [pwBusy, setPwBusy] = React.useState(false);
    const [email, setEmail] = React.useState(c.email || '');
    const [loc, setLoc] = React.useState(c.target_location || '');
    const suspended = c.active === 0;
    const isAdminClient = !!c.is_admin;

    const dirty = plan !== (c.plan || 'starter') || status !== (c.status || 'trial')
      || Number(limit) !== (c.domain_limit ?? 1) || Number(discount) !== (c.discount_pct ?? 0)
      || notes !== (c.admin_notes || '') || Number(kw) !== (c.keyword_limit ?? 25) || Number(rank) !== (c.ranking_limit ?? 25)
      || wl !== !!c.whitelabel || brandName !== (c.brand_name || '') || accent !== (c.accent_color || '#2563EB')
      || wlEmail !== (c.support_email || '') || wlPhone !== (c.support_phone || '') || logo !== (c.logo_url || '')
      || email.trim() !== (c.email || '') || loc.trim() !== (c.target_location || '');

    const save = () => {
      if (busy) return;
      setBusy(true); setMsg(null);
      window.SGLive.adminUpdateClient(c.id, {
        plan, status, domain_limit: Number(limit), discount_pct: Number(discount), admin_notes: notes,
        keyword_limit: Number(kw), ranking_limit: Number(rank), whitelabel: wl,
        brand_name: brandName, accent_color: accent, support_email: wlEmail, support_phone: wlPhone, logo_url: logo,
        email: email.trim(), target_location: loc.trim(),
      }).then((r) => {
        setBusy(false);
        if (r && r.success) { setMsg('Saved'); onSaved && onSaved(r.data); }
        else setMsg('Error: ' + ((r && r.error) || 'failed'));
      });
    };
    const issue = () => {
      window.SGLive.adminIssueLink(c.id).then((d) => { if (d) { setLink(d.access_url); try { navigator.clipboard.writeText(d.access_url); } catch (e) { /* noop */ } } });
    };
    const setPassword = () => {
      if (pwBusy) return;
      if (pwVal.trim().length < 6) { setMsg('Error: password ≥ 6 chars'); return; }
      setPwBusy(true); setMsg(null);
      window.SGLive.adminSetPassword(c.id, pwVal.trim()).then((r) => {
        setPwBusy(false);
        if (r && r.success) { setMsg('Password set'); setPwVal(''); }
        else setMsg('Error: ' + ((r && r.error) || 'failed'));
      });
    };
    const toggleSuspend = () => {
      if (actBusy) return;
      setActBusy(true); setMsg(null);
      window.SGLive.adminUpdateClient(c.id, { active: suspended }).then((r) => {
        setActBusy(false);
        if (r && r.success) onSaved && onSaved(r.data);
        else setMsg('Error: ' + ((r && r.error) || 'failed'));
      });
    };
    const del = () => {
      if (delText.trim().toUpperCase() !== 'DELETE') { setMsg('Type DELETE to confirm'); return; }
      setActBusy(true); setMsg(null);
      window.SGLive.adminDeleteClient(c.id).then((r) => {
        setActBusy(false);
        if (r.ok) onSaved && onSaved();
        else { setMsg('Error: ' + (r.error || 'failed')); setConfirmDel(false); setDelText(''); }
      });
    };
    const { base, net } = netPrice(plan, discount);

    return (
      <Card style={{ padding: 0, opacity: suspended ? 0.62 : 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--sg-border)' }}>
          <span style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--sg-blue-50)', color: 'var(--sg-blue-600)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><Icon name="globe" size={16} /></span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sg-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.business_name || c.domain || c.id}{isAdminClient && <span style={{ marginLeft: 8, fontSize: 9.5, fontWeight: 700, color: 'var(--sg-violet-700)', background: 'var(--sg-violet-100)', padding: '2px 7px', borderRadius: 'var(--sg-radius-pill)' }}>ADMIN</span>}</div>
            <div style={{ fontSize: 12, color: 'var(--sg-text-3)' }}>{c.domain || '—'} · <span className="sg-tnum">{c.id}</span></div>
          </div>
          {suspended && <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', color: 'var(--sg-red, #DC2626)', background: 'var(--sg-red-50, #FDECEC)', padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)' }}>SUSPENDED</span>}
          <StatusPill s={status} />
        </div>

        <div style={{ padding: 18, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          {field('Plan', <select value={plan} onChange={(e) => setPlan(e.target.value)} style={selStyle}>{PLANS.map((p) => <option key={p} value={p}>{p}</option>)}</select>)}
          {field('Status', <select value={status} onChange={(e) => setStatus(e.target.value)} style={selStyle}>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>)}
          {field('Domains', <input type="number" min="0" value={limit} onChange={(e) => setLimit(e.target.value)} style={inStyle} />)}
          {field('Discount %', <input type="number" min="0" max="100" value={discount} onChange={(e) => setDiscount(e.target.value)} style={inStyle} />)}
          {field('Keywords / mo', <input type="number" min="0" value={kw} onChange={(e) => setKw(e.target.value)} style={inStyle} />)}
          {field('Tracked rankings', <input type="number" min="0" value={rank} onChange={(e) => setRank(e.target.value)} style={inStyle} />)}
          <div style={{ gridColumn: '1 / 3' }}>{field('Target location (city / country — drives keyword & AI-visibility geo)', <input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Toronto, Canada" style={inStyle} />)}</div>
        </div>
        <div style={{ padding: '0 18px 14px' }}>
          {field('Notes (why the discount / comp)', <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Founding client — 50% off for 6 months" style={inStyle} />)}
        </div>

        {/* White-label (Agency) */}
        <div style={{ padding: '0 18px 16px' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--sg-text)' }}>
            <input type="checkbox" checked={wl} onChange={(e) => setWl(e.target.checked)} /> White-label this client (Agency)
          </label>
          {wl && (
            <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
              {field('Brand name', <input value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="Acme SEO Co" style={inStyle} />)}
              {field('Accent color', <input value={accent} onChange={(e) => setAccent(e.target.value)} placeholder="#2563EB" style={inStyle} />)}
              {field('Support email', <input value={wlEmail} onChange={(e) => setWlEmail(e.target.value)} placeholder="help@acme.com" style={inStyle} />)}
              {field('Support phone', <input value={wlPhone} onChange={(e) => setWlPhone(e.target.value)} placeholder="+1 555-0100" style={inStyle} />)}
              <div style={{ gridColumn: '1 / -1' }}>{field('Logo URL', <input value={logo} onChange={(e) => setLogo(e.target.value)} placeholder="https://…/logo.png" style={inStyle} />)}</div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderTop: '1px solid var(--sg-border)', background: 'var(--sg-sunken)', flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12.5, color: 'var(--sg-text-2)' }}>
            Price: {discount > 0
              ? <span><span style={{ textDecoration: 'line-through', color: 'var(--sg-text-3)' }}>${base}</span> <strong style={{ color: 'var(--sg-green)' }}>${net}/mo</strong> <span style={{ color: 'var(--sg-text-3)' }}>({discount}% off)</span></span>
              : <strong style={{ color: 'var(--sg-text)' }}>${base}/mo</strong>}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {msg && <span style={{ fontSize: 12, color: msg === 'Saved' ? 'var(--sg-green)' : 'var(--sg-red, #DC2626)' }}>{msg}</span>}
            {!isAdminClient && (
              <button onClick={toggleSuspend} disabled={actBusy} title={suspended ? 'Restore access' : 'Block this client from using the tool'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 12px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', cursor: actBusy ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 600, color: suspended ? 'var(--sg-green)' : 'var(--sg-amber)' }}>
                <Icon name={suspended ? 'check' : 'alert-triangle'} size={13} /> {suspended ? 'Reactivate' : 'Suspend'}
              </button>
            )}
            {!isAdminClient && (
              <button onClick={() => { setConfirmDel((v) => !v); setDelText(''); setMsg(null); }} disabled={actBusy} title="Permanently delete this client and all its data"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 12px', border: '1px solid ' + (confirmDel ? 'var(--sg-red, #DC2626)' : 'var(--sg-border)'), borderRadius: 'var(--sg-radius-pill)', background: confirmDel ? 'var(--sg-red, #DC2626)' : 'var(--sg-card)', cursor: actBusy ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 700, color: confirmDel ? '#fff' : 'var(--sg-red, #DC2626)' }}>
                <Icon name="x" size={13} /> {confirmDel ? 'Cancel delete' : 'Delete'}
              </button>
            )}
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Login email (magic link)"
              title="Account email for passwordless magic-link sign-in. Click Save to apply."
              style={{ height: 34, width: 180, padding: '0 10px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', fontFamily: 'var(--sg-font-sans)', fontSize: 12.5, color: 'var(--sg-text)', outline: 'none' }} />
            <input type="password" value={pwVal} onChange={(e) => setPwVal(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') setPassword(); }} placeholder="Login password"
              style={{ height: 34, width: 130, padding: '0 10px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', fontFamily: 'var(--sg-font-sans)', fontSize: 12.5, color: 'var(--sg-text)', outline: 'none' }} />
            <button onClick={setPassword} disabled={pwBusy} title="Set the client's login password (they sign in at /?login=…)"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 12px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--sg-text-2)' }}><Icon name="lock" size={13} /> {pwBusy ? 'Setting…' : 'Set password'}</button>
            <button onClick={issue} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 12px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--sg-text-2)' }}><Icon name="link" size={13} /> {link ? 'Copied!' : 'Access link'}</button>
            <button onClick={save} disabled={!dirty || busy} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 16px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: dirty && !busy ? 'pointer' : 'default', opacity: dirty && !busy ? 1 : 0.5, fontWeight: 700, fontSize: 13, color: '#fff', background: 'var(--sg-grad-brand)' }}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
        {confirmDel && !isAdminClient && (
          <div style={{ margin: '0 18px 14px', padding: 14, borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-red-50, #FDECEC)', border: '1px solid var(--sg-red-100, #F8C9C9)' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <Icon name="alert-triangle" size={18} color="var(--sg-red, #DC2626)" style={{ flex: 'none', marginTop: 1 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--sg-red, #DC2626)', marginBottom: 4 }}>Permanently delete {c.business_name || c.domain || c.id}?</div>
                <div style={{ fontSize: 12.5, color: 'var(--sg-text-2)', lineHeight: 1.5, marginBottom: 10 }}>
                  This <strong>erases everything</strong> for this client — all tracked domains, Google/Bing connections, keywords, rankings and reports. It cannot be undone. To just reset their login, use <strong>Set password</strong> instead — that keeps all their data.
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input value={delText} onChange={(e) => setDelText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') del(); }} placeholder="Type DELETE to confirm" autoFocus
                    style={{ height: 34, width: 190, padding: '0 12px', border: '1px solid var(--sg-red-200, #F0A9A9)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', fontFamily: 'var(--sg-font-sans)', fontSize: 12.5, color: 'var(--sg-text)', outline: 'none' }} />
                  <button onClick={del} disabled={actBusy || delText.trim().toUpperCase() !== 'DELETE'}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 16px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: (actBusy || delText.trim().toUpperCase() !== 'DELETE') ? 'not-allowed' : 'pointer', opacity: (actBusy || delText.trim().toUpperCase() !== 'DELETE') ? 0.5 : 1, fontWeight: 700, fontSize: 12.5, color: '#fff', background: 'var(--sg-red, #DC2626)' }}>
                    <Icon name="x" size={13} /> {actBusy ? 'Deleting…' : 'Permanently delete'}
                  </button>
                  <button onClick={() => { setConfirmDel(false); setDelText(''); }} style={{ height: 34, padding: '0 14px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--sg-text-2)' }}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {link && <div style={{ padding: '0 18px 14px', fontSize: 11.5, color: 'var(--sg-text-3)', wordBreak: 'break-all' }}>{link}</div>}
      </Card>
    );
  }

  function AddClient({ onCreated }) {
    const [open, setOpen] = React.useState(false);
    const [domain, setDomain] = React.useState('');
    const [name, setName] = React.useState('');
    const [plan, setPlan] = React.useState('starter');
    const [busy, setBusy] = React.useState(false);
    const [err, setErr] = React.useState(null);
    const [result, setResult] = React.useState(null);

    const create = () => {
      const d = domain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '');
      if (!d || d.indexOf('.') < 0) { setErr('Enter a valid domain like example.com'); return; }
      setBusy(true); setErr(null);
      window.SGLive.adminCreateClient({ domain: d, business_name: name.trim() || undefined }).then((r) => {
        if (!r.ok) { setBusy(false); setErr(r.error || 'Could not create client'); return; }
        // apply the chosen plan + make it active so it's usable immediately
        window.SGLive.adminUpdateClient(r.data.client_id, { plan, status: 'active' }).then(() => {
          setBusy(false);
          setResult({ link: 'https://rank.aibizconnect.ca/?key=' + r.data.api_key });
          try { navigator.clipboard.writeText('https://rank.aibizconnect.ca/?key=' + r.data.api_key); } catch (e) { /* noop */ }
          setDomain(''); setName('');
          onCreated && onCreated();
        });
      });
    };

    if (!open) {
      return (
        <button onClick={() => { setOpen(true); setResult(null); }} style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 7, height: 42, padding: '0 18px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 14, color: '#fff', background: 'var(--sg-grad-brand)', boxShadow: '0 3px 10px rgba(37,99,235,0.26)' }}>
          <Icon name="plus" size={16} /> New client
        </button>
      );
    }
    return (
      <Card>
        <CardHead title="Add a standalone client" sub="For clients outside GoHighLevel — issues a private ?key= access link. For GHL sub-accounts, use “GHL sub-accounts” above instead." />
        <div style={{ padding: 18, display: 'grid', gridTemplateColumns: '1.4fr 1.4fr 1fr auto', gap: 12, alignItems: 'end' }}>
          {field('Domain', <input value={domain} onChange={(e) => setDomain(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') create(); }} placeholder="example.com" style={inStyle} />)}
          {field('Business name (optional)', <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Co" style={inStyle} />)}
          {field('Plan', <select value={plan} onChange={(e) => setPlan(e.target.value)} style={{ ...selStyle, width: '100%' }}>{PLANS.map((p) => <option key={p} value={p}>{p} (${PLAN_PRICE[p]})</option>)}</select>)}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={create} disabled={busy} style={{ height: 38, padding: '0 18px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13, color: '#fff', background: 'var(--sg-grad-brand)' }}>{busy ? 'Creating…' : 'Create'}</button>
            <button onClick={() => { setOpen(false); setErr(null); }} style={{ height: 38, padding: '0 14px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--sg-text-2)' }}>Cancel</button>
          </div>
        </div>
        {err && <div style={{ padding: '0 18px 14px', fontSize: 12.5, color: 'var(--sg-amber)' }}>{err}</div>}
        {result && (
          <div style={{ margin: '0 18px 16px', padding: 14, borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-green-50)', border: '1px solid var(--sg-green-100, #CDEBD6)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--sg-text)', marginBottom: 4 }}>✓ Client created — access link copied to clipboard</div>
            <div style={{ fontSize: 12, color: 'var(--sg-text-2)', wordBreak: 'break-all' }}>{result.link}</div>
          </div>
        )}
      </Card>
    );
  }

  // A memorable temp password (no ambiguous chars). The client changes it on first login.
  function genTempPassword() {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
    let s = '';
    for (let i = 0; i < 8; i++) s += chars[Math.floor(Math.random() * chars.length)];
    return 'Sg-' + s;
  }
  function fmtAgo(ts) {
    if (!ts) return '';
    const d = Math.max(0, Math.floor(Date.now() / 1000) - ts);
    if (d < 60) return 'just now';
    if (d < 3600) return Math.floor(d / 60) + 'm ago';
    if (d < 86400) return Math.floor(d / 3600) + 'h ago';
    return Math.floor(d / 86400) + 'd ago';
  }

  // Inline enroll panel — shared by the pending list and the manual add form.
  function EnrollPanel({ locationId, businessName, lockLocation, onDone }) {
    const [loc, setLoc] = React.useState(locationId || '');
    const [name, setName] = React.useState(businessName || '');
    const [plan, setPlan] = React.useState('growth');
    const [trial, setTrial] = React.useState(false);
    const [pw, setPw] = React.useState(genTempPassword());
    const [busy, setBusy] = React.useState(false);
    const [err, setErr] = React.useState(null);
    const [done, setDone] = React.useState(null);

    const enroll = () => {
      if (busy) return;
      const L = (loc || '').trim();
      if (!/^[A-Za-z0-9]{6,40}$/.test(L)) { setErr('Enter a valid GHL Location ID (letters/numbers).'); return; }
      if ((pw || '').trim().length < 6) { setErr('Temp password must be at least 6 characters.'); return; }
      setBusy(true); setErr(null);
      window.SGLive.adminEnroll({ locationId: L, business_name: name.trim() || undefined, plan, trial, password: pw.trim() }).then((r) => {
        setBusy(false);
        if (!r.ok) { setErr(r.error || 'Could not enroll this sub-account.'); return; }
        setDone({ login_url: r.data.login_url, password: pw.trim(), client_id: r.data.client_id });
        try { navigator.clipboard.writeText('Sign in: ' + r.data.login_url + '\nTemporary password: ' + pw.trim()); } catch (e) { /* noop */ }
        if (onDone) onDone();
      });
    };

    if (done) {
      return (
        <div style={{ padding: 14, borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-green-50)', border: '1px solid var(--sg-green-100, #CDEBD6)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--sg-text)', marginBottom: 8 }}>✓ Enrolled — details copied to clipboard. Send these to the client:</div>
          <div style={{ fontSize: 12.5, color: 'var(--sg-text-2)', lineHeight: 1.7 }}>
            <div><strong>Temporary password:</strong> <span className="sg-tnum" style={{ fontWeight: 700, color: 'var(--sg-text)' }}>{done.password}</span> <span style={{ color: 'var(--sg-text-3)' }}>— they’ll be asked to choose their own on first sign-in.</span></div>
            <div style={{ marginTop: 4 }}><strong>They sign in by</strong> opening the <em>SEO / GEO Suite</em> tab in their GoHighLevel sub-account (or directly: <span style={{ wordBreak: 'break-all', color: 'var(--sg-blue-600)' }}>{done.login_url}</span>).</div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1.3fr 0.9fr', gap: 12 }}>
          {field('GHL Location ID', <input value={loc} onChange={(e) => setLoc(e.target.value)} disabled={lockLocation} placeholder="e.g. yDwou55cNKwHB5cg0Frs" style={{ ...inStyle, opacity: lockLocation ? 0.7 : 1 }} />)}
          {field('Business name (optional)', <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Co" style={inStyle} />)}
          {field('Plan', <select value={plan} onChange={(e) => setPlan(e.target.value)} style={{ ...selStyle, width: '100%' }}>{PLANS.map((p) => <option key={p} value={p}>{p} (${PLAN_PRICE[p]})</option>)}</select>)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr auto auto', gap: 12, alignItems: 'end' }}>
          {field('Temporary password', (
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={pw} onChange={(e) => setPw(e.target.value)} style={{ ...inStyle, flex: 1 }} />
              <button type="button" onClick={() => setPw(genTempPassword())} title="Generate a new one" style={{ height: 38, padding: '0 12px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-card)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--sg-text-2)' }}>↻</button>
            </div>
          ))}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 38, fontSize: 13, fontWeight: 600, color: 'var(--sg-text-2)', cursor: 'pointer' }}>
            <input type="checkbox" checked={trial} onChange={(e) => setTrial(e.target.checked)} /> 14-day trial
          </label>
          <button onClick={enroll} disabled={busy} style={{ height: 38, padding: '0 18px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13, color: '#fff', background: 'var(--sg-grad-brand)' }}>{busy ? 'Enrolling…' : 'Enroll'}</button>
        </div>
        {err && <div style={{ fontSize: 12.5, color: 'var(--sg-amber)' }}>{err}</div>}
      </div>
    );
  }

  function PendingRow({ p, onChanged }) {
    const [open, setOpen] = React.useState(false);
    const [dismissing, setDismissing] = React.useState(false);
    const dismiss = () => {
      setDismissing(true);
      window.SGLive.adminDismissPending(p.location_id).then(() => { if (onChanged) onChanged(); });
    };
    return (
      <div style={{ padding: '12px 16px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-card)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--sg-amber-50)', color: 'var(--sg-amber)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><Icon name="clock" size={15} /></span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--sg-text)' }}>{p.business_name || 'GHL sub-account'}</div>
            <div style={{ fontSize: 12, color: 'var(--sg-text-3)' }}><span className="sg-tnum">{p.location_id}</span> · seen {fmtAgo(p.last_seen)}{p.hits > 1 ? ` · ${p.hits}×` : ''}</div>
          </div>
          <button onClick={() => setOpen((v) => !v)} style={{ height: 32, padding: '0 14px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: '#fff', background: 'var(--sg-grad-brand)' }}>{open ? 'Close' : 'Enroll'}</button>
          <button onClick={dismiss} disabled={dismissing} title="Remove from this list" style={{ height: 32, padding: '0 12px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--sg-text-3)' }}>Dismiss</button>
        </div>
        {open && <div style={{ marginTop: 14 }}><EnrollPanel locationId={p.location_id} businessName={p.business_name} lockLocation onDone={onChanged} /></div>}
      </div>
    );
  }

  function GhlEnroll({ onChanged }) {
    const [pending, setPending] = React.useState(null);
    const [manual, setManual] = React.useState(false);
    const load = React.useCallback(() => window.SGLive.adminListPending().then(setPending), []);
    React.useEffect(() => { load(); }, [load]);
    const refresh = () => { setPending(null); load(); if (onChanged) onChanged(); };

    return (
      <Card>
        <CardHead title="GHL sub-accounts" sub="Enroll a GoHighLevel sub-account into the ranking tool. Subs that opened the SEO / GEO tab but aren’t set up yet show here automatically."
          right={<button onClick={refresh} style={{ fontSize: 12, fontWeight: 600, color: 'var(--sg-blue-600)', background: 'none', border: 'none', cursor: 'pointer' }}>Refresh</button>} />
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {pending == null && <div style={{ fontSize: 13, color: 'var(--sg-text-2)' }}>Loading…</div>}
          {pending && pending.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--sg-text-2)', lineHeight: 1.55 }}>
              No sub-accounts are waiting. When a new sub opens the <em>SEO / GEO Suite</em> tab, it appears here — or add one by its Location ID below.
            </div>
          )}
          {pending && pending.map((p) => <PendingRow key={p.location_id} p={p} onChanged={refresh} />)}

          <div style={{ borderTop: '1px solid var(--sg-border)', paddingTop: 14 }}>
            {!manual
              ? <button onClick={() => setManual(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 14px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--sg-text-2)' }}><Icon name="plus" size={15} /> Enroll by Location ID</button>
              : <EnrollPanel onDone={refresh} />}
          </div>
        </div>
        <div style={{ margin: '0 18px 16px', padding: 12, borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-blue-50)', border: '1px solid var(--sg-blue-100, #D6E4FF)', fontSize: 12, color: 'var(--sg-text-2)', lineHeight: 1.55 }}>
          <strong>Where do I find the Location ID?</strong> In GoHighLevel, open the sub-account → <em>Settings → Business Info</em> (the Location ID is in the URL and settings). This is the id the SEO / GEO tab uses to sign the client in automatically.
        </div>
      </Card>
    );
  }

  // Invite a friend / prospect by email — creates a free 14-day trial and emails
  // them a private magic sign-in link so they can start using it right away.
  function InviteClient({ onInvited }) {
    const [email, setEmail] = React.useState('');
    const [name, setName] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [result, setResult] = React.useState(null);
    const [err, setErr] = React.useState('');
    const send = () => {
      const e = email.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { setErr('Enter a valid email address.'); return; }
      setErr(''); setBusy(true); setResult(null);
      window.SGLive.adminInvite({ email: e, business_name: name.trim() || undefined }).then((r) => {
        setBusy(false);
        if (r.ok) {
          setResult(r.data); setEmail(''); setName('');
          if (r.data && r.data.link) { try { navigator.clipboard.writeText(r.data.link); } catch (x) { /* noop */ } }
          if (onInvited) onInvited();
        } else setErr(r.error || 'Could not send the invite.');
      });
    };
    return (
      <Card>
        <CardHead title="Invite someone to try it" sub="Emails a private sign-in link and starts a free 14-day trial — perfect for letting a friend or prospect check out the system. No card required for them." icon="send" />
        <div style={{ padding: 18, display: 'grid', gridTemplateColumns: '1.4fr 1.4fr auto', gap: 12, alignItems: 'end' }}>
          {field('Their email', <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }} placeholder="friend@email.com" style={inStyle} />)}
          {field('Name / business (optional)', <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Co" style={inStyle} />)}
          <button onClick={send} disabled={busy} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 38, padding: '0 18px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13, color: '#fff', background: 'var(--sg-grad-brand)' }}><Icon name="send" size={14} /> {busy ? 'Sending…' : 'Send invite'}</button>
        </div>
        {err && <div style={{ padding: '0 18px 14px', fontSize: 12.5, color: 'var(--sg-amber)' }}>{err}</div>}
        {result && (
          <div style={{ margin: '0 18px 16px', padding: 14, borderRadius: 'var(--sg-radius-md)', background: result.sent ? 'var(--sg-green-50)' : 'var(--sg-amber-50)', border: '1px solid ' + (result.sent ? 'var(--sg-green-100, #CDEBD6)' : 'var(--sg-amber-100, #FBE5C8)') }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--sg-text)', marginBottom: 4 }}>
              {result.sent ? '✓ Invite emailed to ' + result.email + ' — sign-in link also copied to your clipboard.' : '⚠ Email isn’t set up yet, so nothing was sent. The sign-in link is copied to your clipboard — paste it to your friend:'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--sg-text-2)', wordBreak: 'break-all' }}>{result.link}</div>
          </div>
        )}
      </Card>
    );
  }

  function Admin() {
    const [clients, setClients] = React.useState(null);
    const [q, setQ] = React.useState('');
    const load = React.useCallback(() => window.SGLive.adminListClients().then(setClients), []);
    React.useEffect(() => { load(); }, [load]);

    const list = (clients || []).filter((c) => {
      if (!q) return true;
      const s = (c.business_name || '') + ' ' + (c.domain || '') + ' ' + (c.id || '');
      return s.toLowerCase().includes(q.toLowerCase());
    });
    const counts = (clients || []).reduce((a, c) => { a[c.status || 'trial'] = (a[c.status || 'trial'] || 0) + 1; return a; }, {});

    return (
      <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <PageHead eyebrow="Admin" title="Client management" icon="users" sub="Override plans, comp or discount any client, and issue access links."
          right={<input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients…" style={{ height: 40, padding: '0 14px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', fontFamily: 'var(--sg-font-sans)', fontSize: 13.5, color: 'var(--sg-text)', minWidth: 220 }} />} />

        <Card style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', overflow: 'hidden' }}>
          {[['Total', (clients || []).length, 'var(--sg-text)'], ['Active', counts.active || 0, 'var(--sg-green)'], ['Trial', counts.trial || 0, 'var(--sg-amber)'], ['Canceled', counts.canceled || 0, 'var(--sg-text-3)']].map(([l, v, col], i) => (
            <div key={l} style={{ padding: '16px 20px', borderLeft: i ? '1px solid var(--sg-border)' : 'none' }}>
              <div className="sg-tnum" style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 24, color: col, lineHeight: 1 }}>{v}</div>
              <div style={{ fontSize: 12, color: 'var(--sg-text-2)', marginTop: 5 }}>{l}</div>
            </div>
          ))}
        </Card>

        <InviteClient onInvited={() => load()} />

        <GhlEnroll onChanged={() => load()} />

        <AddClient onCreated={() => load()} />

        {clients == null
          ? <Card style={{ padding: 22, fontSize: 13, color: 'var(--sg-text-2)' }}>Loading clients…</Card>
          : list.length === 0
            ? <Card style={{ padding: 22, fontSize: 13, color: 'var(--sg-text-2)' }}>No clients{q ? ' match your search' : ' yet'}.</Card>
            : list.map((c) => <ClientCard key={c.id} c={c} onSaved={() => load()} />)}

        <Card style={{ padding: 16, display: 'flex', gap: 12, alignItems: 'flex-start', background: 'var(--sg-blue-50)', border: '1px solid var(--sg-blue-100, #D6E4FF)' }}>
          <Icon name="info" size={18} color="var(--sg-blue-600)" />
          <div style={{ fontSize: 12.5, color: 'var(--sg-text)', lineHeight: 1.55 }}>
            Discounts here record the agreed rate and unlock access immediately. To actually reduce what the client is <strong>charged</strong>, apply the matching coupon on their GoHighLevel order form / subscription — access is granted regardless of the amount paid.
          </div>
        </Card>

        <AuditLog />
      </div>
    );
  }

  const AUDIT_LABEL = {
    'auth.login': 'Signed in', 'auth.login_failed': 'Failed sign-in', 'client.create': 'Client created',
    'client.provision': 'Client provisioned', 'client.patch': 'Client updated', 'client.password_set': 'Password set',
    'client.delete': 'Client deleted', 'access_link.issue': 'Access link issued', 'apikey.rotate': 'API key rotated',
  };
  function fmtAuditTime(ts) { try { return new Date(ts * 1000).toLocaleString(); } catch (e) { return ''; } }
  function AuditLog() {
    const [rows, setRows] = React.useState(null);
    const load = () => window.SGLive.adminAuditLog(80).then(setRows);
    React.useEffect(() => { load(); }, []);
    return (
      <Card>
        <CardHead title="Activity log" sub="Recent admin actions & sign-ins — audit trail (ISO 27001)"
          right={<button onClick={() => { setRows(null); load(); }} style={{ fontSize: 12, fontWeight: 600, color: 'var(--sg-blue-600)', background: 'none', border: 'none', cursor: 'pointer' }}>Refresh</button>} />
        <div style={{ maxHeight: 340, overflowY: 'auto' }}>
          {rows == null && <div style={{ padding: 16, fontSize: 13, color: 'var(--sg-text-2)' }}>Loading…</div>}
          {rows && rows.length === 0 && <div style={{ padding: 16, fontSize: 13, color: 'var(--sg-text-2)' }}>No activity recorded yet.</div>}
          {rows && rows.map((r, i) => {
            const failed = r.action === 'auth.login_failed';
            return (
              <div key={r.id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 18px', borderTop: i ? '1px solid var(--sg-border)' : 'none' }}>
                <span style={{ fontSize: 12, fontWeight: 700, minWidth: 128, color: failed ? 'var(--sg-red, #DC2626)' : (r.is_master ? 'var(--sg-violet-700)' : 'var(--sg-text)') }}>{AUDIT_LABEL[r.action] || r.action}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--sg-text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.target || ''}{r.is_master ? ' · admin' : ''}{r.ip ? ' · ' + r.ip : ''}</span>
                <span className="sg-tnum" style={{ fontSize: 11.5, color: 'var(--sg-text-3)', flex: 'none' }}>{fmtAuditTime(r.created_at)}</span>
              </div>
            );
          })}
        </div>
      </Card>
    );
  }

  window.SGAdmin = Admin;
})();
